import { ChangeEvent, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Theme, Tab, Tabs } from '@mui/material';
import AdminMessagesTablePanel from './AdminMessagesTable/AdminMessagesTablePanel';
import {
  AdminMessagesTableFilteredData,
  AdminMessagesTableReceivedData,
  AdminMessagesTableSentData,
} from './AdminMessagesTable/AdminMessagesTableData';
import clsx from 'clsx';
import { AdminMessagesContextProvider } from './AdminMessagesContext';
import { Message } from 'types/Message';
import AdminMessagesDeleteButton from './AdminMessagesDeleteButton/AdminMessagesDeleteButton';
import AdminMessagesContainer from './AdminMessagesContainer';
import { useSelectedLinkContext } from 'contexts/SelectedLinkContext';
import { makeStyles } from '@mui/styles';
import { adminFontFamily, COLORS } from 'theme/MainTheme';

const useStyles = makeStyles((theme: Theme) => ({
  firstTab: {
    borderBottom: `1px solid ${COLORS.DARKER_BLUE}`,

    [theme.breakpoints.up('md')]: {
      borderRadius: '10px 0 0 0',
    },
  },
  lastTab: {
    borderBottom: `1px solid ${COLORS.DARKER_BLUE}`,

    [theme.breakpoints.up('md')]: {
      borderRadius: '0 10px 0 0',
    },

    '&.MuiButtonBase-root': {
      borderLeft: 0,
    },
  },
  middleTab: {
    border: `1px solid ${COLORS.DARKER_BLUE}`,
    borderTop: 'none',
  },
  tab: {
    fontFamily: adminFontFamily,
    fontSize: 15,
    fontWeight: 400,
    textTransform: 'capitalize',
  },
  tabSelector: {
    [theme.breakpoints.up('md')]: {
      '& .MuiTabs-flexContainer': {
        justifyContent: 'end',
      },
    },

    '& .MuiButtonBase-root.Mui-selected': {
      color: COLORS.WHITE,
      background: COLORS.DARKER_BLUE,
    },
    '& .MuiButtonBase-root': {
      color: COLORS.DARKER_BLUE,
      background: COLORS.WHITE,
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
}));

type AdminMessagesPureProps = {
  currentTab: number;
  handleCurrentTabChange: (_e: ChangeEvent, value: number) => void;
};

const AdminMessagesPure = ({
  currentTab,
  handleCurrentTabChange,
}: AdminMessagesPureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <AdminMessagesContainer>
      <AdminMessagesDeleteButton />

      <Tabs
        className={classes.tabSelector}
        value={currentTab}
        onChange={handleCurrentTabChange}
      >
        <Tab
          className={clsx(classes.tab, classes.firstTab)}
          label={<FormattedMessage id="admin.messages.filter.received" />}
        />
        <Tab
          className={clsx(classes.tab, classes.middleTab)}
          label={<FormattedMessage id="admin.messages.filter.sent" />}
        />
        <Tab
          className={clsx(classes.tab, classes.lastTab)}
          label={<FormattedMessage id="admin.messages.filter.filtered" />}
        />
      </Tabs>

      <AdminMessagesTablePanel currentValue={currentTab} value={0}>
        <AdminMessagesTableReceivedData />
      </AdminMessagesTablePanel>

      <AdminMessagesTablePanel currentValue={currentTab} value={1}>
        <AdminMessagesTableSentData />
      </AdminMessagesTablePanel>

      <AdminMessagesTablePanel currentValue={currentTab} value={2}>
        <AdminMessagesTableFilteredData />
      </AdminMessagesTablePanel>
    </AdminMessagesContainer>
  );
};

const AdminMessages = (): JSX.Element | null => {
  const { selectedLink } = useSelectedLinkContext();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [selectedMessages, setSelectedMessages] = useState<Message[]>([]);

  const handleCurrentTabChange = useCallback(
    (_e: ChangeEvent, value: number) => {
      if (value !== currentTab) {
        setSelectedMessages([]);
      }

      setCurrentTab(value);
    },
    [currentTab, setCurrentTab, setSelectedMessages],
  );

  if (!selectedLink?.mainUser) {
    return null;
  }

  return (
    <AdminMessagesContextProvider
      selectedMessages={selectedMessages}
      setSelectedMessages={setSelectedMessages}
    >
      <AdminMessagesPure
        currentTab={currentTab}
        handleCurrentTabChange={handleCurrentTabChange}
      />
    </AdminMessagesContextProvider>
  );
};

export default AdminMessages;
